import { FunctionComponent, useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import LogoFull from "../components/logo-full"
import { useLoginUserPlainMutation } from "../app/services/meseeknaApiV2"
import { useTypedDispatch } from "../app/store"
import { setRole, updateOrganization } from "../features/auth/authSlice"
import { loginUser } from "../features/auth/authThunks"
import { Organization } from "../features/auth/types"

const LoginOtp: FunctionComponent = () => {
  const navigate = useNavigate()
  const dispatch = useTypedDispatch()
  const [searchParams] = useSearchParams()

  const [loginUserPlain] = useLoginUserPlainMutation()

  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)

  // Get token and email from URL parameters
  const token = searchParams.get("token") || ""
  const email = searchParams.get("email") || ""

  const handleDirectLogin = async () => {
    if (!token || !email) {
      setError(
        "Invalid login link. Please use the direct link from your email.",
      )
      setIsLoading(false)
      return
    }

    setIsProcessing(true)
    try {
      console.log(
        "Attempting OTP login with token:",
        token.substring(0, 5) + "...",
      )

      // Use the existing login endpoint with OTP token as password
      const result = await loginUserPlain({
        username: email,
        password: token, // Use OTP token as password
      }).unwrap()

      if (result.code === 200 && result.data?.[0]) {
        const userData = result.data[0]

        // Use the loginUser thunk to handle user authentication
        dispatch(loginUser(userData))

        // Create organization data from user data
        const organizationData: Organization = {
          id: userData.organizationId?.toString() || "",
          name: userData.organizationName || "",
          organizationName: userData.organizationName || "",
          role: userData.role?.toString() || "",
          status: "success",
          bodyMessage: "",
          fields: [],
        }

        // Set organization and role data
        dispatch(updateOrganization(organizationData))
        dispatch(setRole({ roleId: userData.role, name: userData.roleName }))

        // Navigate to dashboard
        navigate("/dashboard/simulations")
      } else {
        setError(
          result.message ||
            "Login failed. Please try again or contact support.",
        )
      }
    } catch (err) {
      const error = err as { data?: { message?: string } }
      console.error("Error during OTP login:", err)
      setError(
        error.data?.message ||
          "Your login link has expired or is invalid. Please contact your administrator for a new login link.",
      )
    } finally {
      setIsLoading(false)
      setIsProcessing(false)
    }
  }

  // Automatically attempt login when component mounts
  useEffect(() => {
    handleDirectLogin()
  }, []) // eslint-disable-line

  const handleRetry = () => {
    setError("")
    setIsLoading(true)
    handleDirectLogin()
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-col pt-[100px] px-8 pb-12 box-border items-center justify-start">
      <main className="w-[460px] flex flex-col items-center justify-start gap-[32px] text-left text-sm text-gray-900 font-text-md-regular1">
        <div className="self-stretch flex flex-col items-center justify-start gap-[24px] text-center font-text-md-regular">
          <LogoFull
            dimensions="/v3/assets/meseekna-logo@2x.png"
            logoWrapWidth="189px"
            logoWrapHeight="31.5px"
            image1Width="188.61px"
            image1Height="30.02px"
          />
          <div className="self-stretch flex flex-col items-start justify-start gap-[12px]">
            <h1 className="m-0 self-stretch relative text-2xl leading-[48px] font-semibold font-inherit">
              {isLoading
                ? "Logging you in..."
                : error
                  ? "Login Failed"
                  : "Welcome to Meseekna"}
            </h1>
            <div className="self-stretch relative text-base leading-[28px] font-text-md-regular1 text-gray-600">
              {isLoading
                ? "Please wait while we process your login link."
                : error
                  ? "There was an issue with your login link."
                  : "You have been successfully logged in."}
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <div className="text-sm text-gray-500">
              {isProcessing ? "Processing login..." : "Initializing..."}
            </div>
          </div>
        )}

        {error && (
          <div className="self-stretch flex flex-col gap-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <ul className="pl-4">
                <li>{error}</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleRetry}
                className="cursor-pointer w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                Try Again
              </button>

              <button
                onClick={() => navigate("/login")}
                className="cursor-pointer w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                Go to Regular Login
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default LoginOtp
